import { createMuiTheme } from '@material-ui/core/styles';
import createPalette from '@material-ui/core/styles/createPalette';
import createTypography from '@material-ui/core/styles/createTypography';

const defaultTheme = createMuiTheme();
const fontWeightBold = 900;

const palette = createPalette({
  primary: { main: '#0000aa' },
  secondary: { main: '#299cd4' },
  text: {
    primary: '#343434',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0,0,0,0.38)'
  },
  grey: {
    900: '#141414',
    800: '#343434',
    700: '#525252',
    600: '#8c8c8c',
    light: '#dadce0'
  },
  background: {
    default: '#fafafa',
    paper: '#fff',
    warm: '#f5f5f5'
  },
  border: {
    box: '#dadce0',
    outlinedButton: 'rgba(0,0,0,0.12)'
  }
});

const typography = createTypography(palette, {
  useNextVariants: true,
  h1: {
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0),
    fontSize: defaultTheme.typography.pxToRem(48)
  },
  h2: {
    color: defaultTheme.palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0.25),
    fontSize: defaultTheme.typography.pxToRem(34)
  },
  h3: {
    color: defaultTheme.palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0),
    fontSize: defaultTheme.typography.pxToRem(24)
  },
  h4: {
    color: defaultTheme.palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.25),
    fontSize: defaultTheme.typography.pxToRem(20)
  },
  h5: {
    //navigation1
    color: defaultTheme.palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0.22),
    fontSize: defaultTheme.typography.pxToRem(18)
  },
  h6: {
    //navigation2
    color: palette.text.primary,
    fontWeight: fontWeightBold,
    letterSpacing: defaultTheme.typography.pxToRem(0.22),
    fontSize: defaultTheme.typography.pxToRem(18)
  },
  body1: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightLight,
    letterSpacing: defaultTheme.typography.pxToRem(0.5),
    fontSize: defaultTheme.typography.pxToRem(16)
  },
  subtitle1: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0),
    fontSize: defaultTheme.typography.pxToRem(16)
  },
  label1: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.15),
    fontSize: defaultTheme.typography.pxToRem(16)
  },
  link1: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.15),
    fontSize: defaultTheme.typography.pxToRem(16)
  },
  body2: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightLight,
    letterSpacing: defaultTheme.typography.pxToRem(0.25),
    fontSize: defaultTheme.typography.pxToRem(14)
  },
  subtitle2: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0),
    fontSize: defaultTheme.typography.pxToRem(14)
  },
  label2: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.1),
    fontSize: defaultTheme.typography.pxToRem(14)
  },
  link2: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.25),
    fontSize: defaultTheme.typography.pxToRem(14)
  },
  caption: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightRegular,
    letterSpacing: defaultTheme.typography.pxToRem(0.4),
    fontSize: defaultTheme.typography.pxToRem(12)
  },
  idText: {
    color: palette.text.primary,
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(0.15),
    fontSize: defaultTheme.typography.pxToRem(12),
    textTransform: 'uppercase'
  },
  button: {
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(1.25),
    fontSize: defaultTheme.typography.pxToRem(14),
    textTransform: 'uppercase'
  },
  breadcrumb: {
    fontWeight: defaultTheme.typography.fontWeightMedium,
    letterSpacing: defaultTheme.typography.pxToRem(2),
    fontSize: defaultTheme.typography.pxToRem(12),
    textTransform: 'uppercase'
  }
});

//Define overrides
let helperTextOverride = { root: { ...typography.caption } };
helperTextOverride.root.color = palette.text.secondary;

const paperOverride = {
  elevation0: {
    border: `1px solid ${palette.border.box}`
  }
};

const buttonOverride = {
  outlinedPrimary: {
    border: `1px solid ${palette.border.outlinedButton}`,
    '&:hover': {
      border: `1px solid ${palette.border.outlinedButton}`
    }
  }
};

const tooltipOverride = {
  tooltip: {
    fontSize: '11px'
  }
};

export default createMuiTheme({
  palette: palette,
  typography: typography,
  overrides: {
    MuiFormHelperText: { ...helperTextOverride },
    MuiPaper: { ...paperOverride },
    MuiButton: { ...buttonOverride },
    MuiTooltip: { ...tooltipOverride }
  }
});
